<template>
  <div>
    <div class="row">
      <div class="col-md-12">

        <div class="form-group">
          <label for="vehicle-plate">Placa*</label>
          <input
              v-model="currentVehicle.plate"
              type="text"
              class="form-control"
              id="vehicle-plate"
              v-mask="'AAA-#X###'"
          >
        </div>

        <div class="form-group">
          <label for="vehicle-renavam">Renavam*</label>
          <input
              :disabled="currentVehicle.id"
              v-model="currentVehicle.renavam"
              :class="currentVehicle.id ? 'not-allowed' : ''"
              type="text"
              class="form-control"
              id="vehicle-renavam"
          >
        </div>

        <div class="form-group">
          <label for="vehicle-chassi">{{ chassiLabel }}</label>
          <input v-model="currentVehicle.chassi" type="text" class="form-control" id="vehicle-chassi">
        </div>

        <div class="form-group">
          <label for="vehicle-cpf-cnpj">CPF/CNPJ do Proprietário*</label>
          <div class="form-check form-switch mb-2">
            <input
                v-model="useCompanyRegistry"
                type="checkbox"
                class="form-check-input"
                id="vehicle-use-company-registry"
            >
            <label class="form-check-label" for="vehicle-use-company-registry">
              Selecionar empresa já cadastrada
            </label>
          </div>

          <select
              v-if="useCompanyRegistry"
              v-model="currentVehicle.cpfCnpj"
              id="vehicle-cpf-cnpj"
              class="form-control"
          >
            <option v-for="company in companiesOptions" :value="company.value" :key="company.label">
              {{ company.label }}
            </option>
          </select>

          <input
              v-else
              v-model="currentVehicle.cpfCnpj"
              type="text"
              class="form-control"
              id="vehicle-cpf-cnpj"
              placeholder="Digite o CPF"
              maxlength="14"
              v-mask="'###.###.###-##'"
          >
        </div>

        <div class="form-group">
          <label>UF*</label>
          <StatesSelect v-model="currentVehicle.uf" class="custom-select"/>
        </div>

        <div class="form-group">
          <label for="vehicle-code-city">Cidade</label>
          <CitySelect
            v-model="currentVehicle.cityCode"
            :code-uf="currentVehicle.uf"
            id="vehicle-code-city"
            class="custom-select"
            :disabled="!currentVehicle.uf"
          />
        </div>

        <div v-if="showOptionCmCrono" class="form-group">
          <label for="vehicle-crono">Cronotacógrafo Habilitado</label>
          <select v-model="currentVehicle.cmCrono" id="vehicle-crono" class="form-control">
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select>
        </div>

        <div v-if="!currentVehicle.id" class="form-group">
          <label for="vehicle-sne">SNE Habilitado</label>
          <select v-model="currentVehicle.sneEnabled" id="vehicle-sne" class="form-control">
            <option :value="false">Não</option>
            <option :value="true">Sim</option>
          </select>
        </div>

        <div v-if="showOptionSne" class="form-group">
          <label for="vehicle-sne-module">Módulo de SNE</label>
          <select v-model="currentVehicle.sneModule" id="vehicle-sne-module" class="form-control">
            <option value="autos_sne_denatran20">SNE 20%</option>
            <option value="autos_sne_denatran40">SNE 40%</option>
          </select>
        </div>

        <div class="form-group">
          <label for="vehicle-security-code">{{ securityCodeLabel }}</label>
          <input v-model="currentVehicle.securityCode" type="text" class="form-control" id="vehicle-security-code">
        </div>

        <div class="form-group">
          <div class="accordion">
            <div class="accordion-header" @click="toggleAccordion">
              <h6 class="pt-1">Dados adicionais (opcional)</h6>
              <div>
                <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': activeAccordion}"/>
              </div>
            </div>

            <div v-if="activeAccordion" class="accordion-body">

              <div class="form-group">
                <label for="vehicle-driver">Motorista</label>
                <input v-model="currentVehicle.driver" type="text" class="form-control" id="vehicle-driver">
              </div>

              <div class="form-group">
                <label for="vehicle-brand">Marca</label>
                <input v-model="currentVehicle.brand" type="text" class="form-control" id="vehicle-brand">
              </div>

              <div class="form-group">
                <label for="vehicle-model">Modelo</label>
                <input v-model="currentVehicle.model" type="text" class="form-control" id="vehicle-model">
              </div>

              <div class="form-group">
                <label for="vehicle-kind">Tipo</label>
                <input v-model="currentVehicle.kind" type="text" class="form-control" id="vehicle-kind">
              </div>

              <div class="form-group">
                <label for="vehicle-application">Utilização</label>
                <input v-model="currentVehicle.application" type="text" class="form-control" id="vehicle-application">
              </div>

              <div class="form-group">
                <label for="vehicle-fleet-code">Código da frota</label>
                <input v-model="currentVehicle.fleetCode" type="text" class="form-control" id="vehicle-fleet-code">
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label>Documentos <CrlvHint/></label>
          <div v-if="isEditing" class="mb-2">
            <span class="badge" :class="crlvBadgeClass">{{ crlvBadgeLabel }}</span>
          </div>
          <div v-if="crlvFile" class="crlv-file-row">
            <span class="crlv-file-name">{{ crlvFile.name }} <small>(CRLV)</small></span>
            <button type="button" class="btn-icon btn-outline" @click="crlvFile = null" title="Remover arquivo">
              <x-icon size="1x"/>
            </button>
          </div>
          <Upload :isMultiple="false" accept=".pdf, .jpg, .jpeg, .png" @changeFiles="selectCrlvFiles">
            <h5 v-if="crlvFile">Clique ou arraste para substituir o arquivo selecionado</h5>
            <h5 v-else>Clique ou arraste para anexar o CRLV - PDF, JPG, PNG (máx 5MB)</h5>
          </Upload>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 text-right">
        <button class="btn btn-outline mr-2" @click="$emit('cancel')">Cancelar</button>
        <button class="btn btn-primary" @click="save" :disabled="disabledSaveButton">Salvar</button>
      </div>
    </div>
  </div>
</template>

<script>
  import CitySelect from '@/components/shares/specialTag/CitySelect'
  import EntityProxy from '@/models/EntityProxy'
  import Formatter from '@/models/Formatter'
  import GetAccount from '@/mixins/GetAccount'
  import GetAccountApps from '@/mixins/GetAccountApps'
  import ModelMapper from '@/mixins/ModelMapper'
  import StatesSelect from '@/components/shares/specialTag/StatesSelect'
  import Upload from '@/components/shares/specialTag/Upload'
  import CrlvHint from '@/components/vehicles/CrlvHint'

  import { ChevronDownIcon, XIcon } from 'vue-feather-icons'
  import { mask } from 'vue-the-mask'

  export default {
    components: { CitySelect, CrlvHint, StatesSelect, ChevronDownIcon, Upload, XIcon },
    computed: {
      crlvBadgeClass() {
        const summary = this.crlvSummary
        if (!summary)
          return 'badge-secondary'

        return summary.licensing_status === 'expired' ? 'badge-danger' : 'badge-success'
      },
      crlvBadgeLabel() {
        const summary = this.crlvSummary
        if (!summary)
          return 'Não anexado'

        return summary.licensing_status === 'expired' ? 'Vencido' : 'Anexado'
      },
      isEditing() {
        return !!(this.vehicle && this.vehicle.id)
      },
      disabledSaveButton() {
        const basicValidation = !this.currentVehicle.uf ||
            !this.currentVehicle.plate

        const renavamEmpty = !this.currentVehicle.renavam ||
            this.currentVehicle.renavam.trim() === '' ||
            this.currentVehicle.renavam === '0000000000-0'

        const chassiRequired = this.isChassisRequired && !this.currentVehicle.chassi
        const securityCodeRequired = this.isSecurityCodeRequired && !this.currentVehicle.securityCode
        const cpfCnpjRequired = !this.currentVehicle.cpfCnpj
        const sneModuleRequired = this.currentVehicle.sneEnabled && !this.currentVehicle.sneModule
        return basicValidation || renavamEmpty || chassiRequired || securityCodeRequired || cpfCnpjRequired || sneModuleRequired
      },
      showOptionCmCrono() {
        return !this.currentVehicle.id && this.getApps.find(app => app.name === 'cm-crono')
      },
      showOptionSne() {
        return this.currentVehicle.sneEnabled === true
      },
      isChassisRequired() {
        return this.currentVehicle.uf === 'MG' || this.currentVehicle.uf === 'TO'
      },
      isSecurityCodeRequired() {
        return this.currentVehicle.uf === 'SE' && !this.currentVehicle.sneEnabled
      },
      chassiLabel() {
        return this.isChassisRequired ? 'Chassi*' : 'Chassi'
      },
      securityCodeLabel() {
        return this.isSecurityCodeRequired ? 'Código de segurança*' : 'Código de segurança'
      }
    },
    created() {
      if (this.vehicle)
        this.currentVehicle = EntityProxy.clone(this.vehicle)
      else {
        this.currentVehicle = EntityProxy.build({ cmCrono: false })
        this.$set(this.currentVehicle, 'sneEnabled', false)
      }

      if (!this.currentVehicle.renavam)
        this.$set(this.currentVehicle, 'renavam', '0000000000-0')

      const cpfCnpjDigits = (this.currentVehicle.cpfCnpj || '').replace(/\D/g, '')
      if (cpfCnpjDigits.length === 14)
        this.useCompanyRegistry = true

      this.buildCompaniesOptions()
      this.loadCrlvSummary()
    },
    data: () => ({
      companiesOptions: null,
      crlvFile: null,
      crlvSummary: null,
      currentVehicle: {},
      nameModel: 'Vehicle',
      activeAccordion: false,
      useCompanyRegistry: false,
    }),
    directives: { mask },
    methods: {
      loadCrlvSummary() {
        if (!this.isEditing) {
          this.crlvSummary = (this.vehicle && this.vehicle.crlvSummary) || null
          return
        }

        this.crlvSummary = (this.vehicle && this.vehicle.crlvSummary) || null
        this.axios.get('vehicle_documents/summary', { params: { vehicle_ids: [this.vehicle.id] } })
          .then(response => {
            const map = (response && response.data) || {}
            this.crlvSummary = map[this.vehicle.id] || map[String(this.vehicle.id)] || null
          })
          .catch(() => {})
      },
      selectCrlvFiles(files) {
        const file = files && files[0]
        if (!file)
          return

        const extension = `.${(file.name.split('.').pop() || '').toLowerCase()}`
        if (!['.pdf', '.jpg', '.jpeg', '.png'].includes(extension)) {
          this.$notify.addError('Formato não suportado. Use PDF, JPG ou PNG')
          return
        }

        if (file.size > 5 * 1024 * 1024) {
          this.$notify.addError('Arquivo muito grande. Tamanho máximo: 5MB')
          return
        }

        this.crlvFile = file
      },
      uploadCrlv(vehicleId) {
        const formData = new FormData()
        formData.append('file', this.crlvFile)

        return this.axios.post(`vehicles/${vehicleId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      },
      toggleAccordion() {
        this.activeAccordion = !this.activeAccordion
      },
      buildCompaniesOptions() {
        const params = {
          per_page: 1000,
          q: { type: ['parent', 'subsidiary'] }
        }
        this.axios.get('companies', { params: params })
          .then(response => response.data)
          .then(data => {
            this.companiesOptions = data
              .map(c => {
                return {
                  label: `${c.name} - ${Formatter.cnpj(c.cnpj)}`,
                  value: c.cnpj
                }
              })
              .sort((a, b) => a.label.trim().localeCompare(b.label.trim(), 'pt-BR'))
          })
          .then(() => this.companiesOptions.unshift({label: '-', value: null}))
      },
      save() {
        if (this.currentVehicle.renavam === '0000000000-0') {
          this.$notify.addError('Renavam inválido')
          return
        }

        if (!this.currentVehicle.cpfCnpj) {
          this.$notify.addError('Informe o CPF/CNPJ do Proprietário')
          return
        }

        this.currentVehicle.cpfCnpj = this.currentVehicle.cpfCnpj.replace(/\D/g, '')

        // Serviços são gerenciados pela aba Serviços. O form nunca deve reenviar o blob
        // de services: se enviasse, o backend pularia o swap automático ao trocar a UF.
        delete this.currentVehicle.services

        this.asyncTemplateSave(this.currentVehicle, {
          after: ({model}) => {
            const finish = message => {
              this.$notify.add(message)
              this.$emit('saved', model)
              this.currentVehicle = model
            }

            if (this.crlvFile && model && model.id) {
              this.uploadCrlv(model.id)
                .then(body => {
                  const notice = body && body.meta && body.meta.notice
                  finish(`Veículo salvo com sucesso! ${notice || 'Documento CRLV anexado'}`)
                })
                .catch(e => {
                  this.$notify.addError(e)
                  finish('Veículo salvo com Sucesso!')
                })
                .finally(() => this.crlvFile = null)
            } else {
              finish('Veículo salvo com Sucesso!')
            }
          }
        })
      },
    },
    mixins: [GetAccount, GetAccountApps, ModelMapper],
    name: 'FormProvider',
    props: {
      vehicle: { default: null, type: Object },
    },
    watch: {
      'currentVehicle.renavam': {
        handler: function(newValue) {
          if (newValue.match(/^\d{10}-\d{1}$/))
            return

          newValue = newValue.replace(/\D+/g, '').padStart(11, '0')
          const diff = 11 - newValue.length
          if (diff < 0)
            newValue = newValue.slice(-diff)

          this.currentVehicle.renavam = `${newValue.substring(0, 10)}-${newValue.substring(10)}`
        },
         deep: true
      },
      'currentVehicle.sneEnabled': {
        handler: function(newValue) {
          if (newValue === true && !this.currentVehicle.sneModule) {
            this.$set(this.currentVehicle, 'sneModule', 'autos_sne_denatran20')
          } else if (newValue === false) {
            this.$set(this.currentVehicle, 'sneModule', null)
          }
        },
        immediate: true
      },
      useCompanyRegistry: {
        handler: function(newValue) {
          if (!newValue) this.$set(this.currentVehicle, 'cpfCnpj', null)
        }
      }
    }
  }
</script>

<style scoped>
  .btn {
    min-width: 134px;
  }

  p {
    color: var(--primary-text-color);
  }

  .accordion {
    border: 1px solid var(--table-border);
    border-radius: 0.25rem;
    margin-bottom: 0.5rem;
  }

  .accordion-header {
    padding: 1rem;
    background-color: var(--background-tab);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    color: var(--primary-text-color);
  }

  .accordion-header h6 {
    color: var(--primary-text-color);
    margin: 0;
  }

  .accordion-body {
    padding: 1rem;
    background-color: var(--white-bg);
  }

  .not-allowed {
    cursor: not-allowed;
  }

  .badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .crlv-file-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #e1e3eb;
    border-radius: 5px;
    padding: 6px 10px;
  }

  .crlv-file-name {
    font-size: 0.85rem;
    color: var(--primary-text-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

</style>
